import { css, SerializedStyles } from '@emotion/react';
import { tokens, applyFont, shadow } from '@domain-group/fe-brary';

const { colors, spacing, typography, borderRadius, shadows } = tokens;

export const COLUMN_GAP = '12px';

// The card swaps between its stacked and side-by-side layouts at 768px, per the
// FAA prototype's card spec. Neither fe-brary nor breakpoints-2026 defines that
// width, so it lives here and is shared by the sibling card style files.
const CARD_LAYOUT_MIN_WIDTH = 768;

export const aboveCardBreakpoint = `@media (min-width: ${CARD_LAYOUT_MIN_WIDTH}px)`;
export const belowCardBreakpoint = `@media (max-width: ${CARD_LAYOUT_MIN_WIDTH - 1}px)`;

export const container = ({
  selected,
}: {
  selected: boolean;
}): SerializedStyles => css`
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  background: ${colors.neutral.surface.default};
  color: ${colors.neutral.heavy.default};
  border: 1px solid
    ${selected ? colors.interactive.trim.selected : colors.neutral.trim.default};
  border-radius: ${borderRadius.outer};
  transition:
    box-shadow 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    box-shadow: ${shadow(shadows.elevation3)};
  }

  &:active {
    box-shadow: ${shadow(shadows.elevation1)};
  }

  &:has(a:focus-visible):not(:active) {
    border-color: ${colors.interactive.trim.selected};
  }
`;

export const body = css`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${spacing.m};
  padding: ${spacing.m};
`;

export const statsSection = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.s};
`;

export const link = css`
  position: absolute;
  inset: 0;
  z-index: 1;
  outline: none;

  @supports not selector(:has(*)) {
    &:focus-visible {
      outline: 2px solid ${colors.interactive.trim.selected};
      outline-offset: -2px;
      border-radius: ${borderRadius.outer};
    }
  }
`;

export const headerRow = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing.m};

  ${belowCardBreakpoint} {
    flex-wrap: wrap;
    row-gap: ${spacing.s};
  }
`;

export const title = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${spacing.xxs};
  min-width: 0;
`;

export const metricsLabel = css`
  ${applyFont(typography.caption)}
  color: ${colors.neutral.medium.default};
`;

export const footnote = css`
  ${applyFont(typography.mini)}
  color: ${colors.neutral.medium.default};
`;

export const name = css`
  ${applyFont(typography.body)}
  color: ${colors.neutral.heavy.default};
`;

export const subtitle = css`
  ${applyFont(typography.caption)}
  color: ${colors.neutral.medium.default};
`;

export const officeLine = css`
  ${applyFont(typography.caption)}
  color: ${colors.neutral.medium.default};
`;

export const officeLineHiddenOnMobile = css`
  ${officeLine}

  ${belowCardBreakpoint} {
    display: none;
  }
`;

export const mediaStacksOnMobile = css`
  ${belowCardBreakpoint} {
    flex-basis: 100%;
  }
`;

export const mobileRating = css`
  ${aboveCardBreakpoint} {
    display: none;
  }
`;

export const statsRow = (columns: number): SerializedStyles => css`
  display: grid;
  grid-template-columns: repeat(${columns}, 1fr);
  align-items: flex-start;
  gap: ${COLUMN_GAP};

  ${belowCardBreakpoint} {
    grid-template-columns: repeat(${columns - 1}, 1fr);
  }
`;

export const rating = css`
  padding-right: ${spacing.m};

  ${belowCardBreakpoint} {
    display: none;
  }
`;

export const footer = css`
  position: relative;
  z-index: 2;
`;
